import { Controller } from "@hotwired/stimulus";
import { loadRevealSDK, initializeRevealAI, updateRevealAIToken, buildScientistTheme, renderInsecureContextError } from "../helpers/reveal_sdk_loader";
import { captureRevealError, sanitizeRevealSaveError } from "../helpers/reveal_sentry";
import { generateUuid } from "../helpers/uuid";

export default class extends Controller {
  static values = {
    serverUrl: String,
    dashboardId: String,
    token: String,
    refreshUrl: String,
    refreshIntervalMinutes: { type: Number, default: 55 },
    canEdit: { type: Boolean, default: false },
    canSaveAs: { type: Boolean, default: false },
    saveAsRedirectUrl: { type: String, default: "" },
    startInEditMode: { type: Boolean, default: false },
    isNew: { type: Boolean, default: false },
    aiEnabled: { type: Boolean, default: false },
    traceparent: { type: String, default: "" },
    datasources: { type: Array, default: [] }
  };

  async connect() {
    if (renderInsecureContextError(this.element)) return;

    try {
      await loadRevealSDK();
    } catch (error) {
      captureRevealError("sdk load", error, { dashboardId: this.dashboardIdValue });
      this.renderLoadFailure();
      return;
    }
    if (this.aiEnabledValue) {
      initializeRevealAI(this.serverUrlValue, () => this.tokenValue);
    }
    this.initializeReveal();
    this.startTokenRefresh();
  }

  // Outlet methods for reveal-ai-chat controller
  getRevealView() {
    return this.revealView;
  }

  getDashboard() {
    return this.revealView?.dashboard;
  }

  async setDashboard(jsonString) {
    const json = JSON.parse(jsonString);
    this.revealView.dashboard = await window.Reveal.RevealUtility.createDashboardFromJsonObject(json);
  }

  initializeReveal() {
    // Reveal 2.x: the `$.ig` global is gone; every type now lives on `window.Reveal`.
    const R = window.Reveal;

    // Configure server URL, theme, and auth headers
    R.RevealSdkSettings.setBaseUrl(this.serverUrlValue);
    R.RevealSdkSettings.theme = buildScientistTheme();
    R.RevealSdkSettings.setAdditionalHeadersProvider(() => {
      const headers = { "Authorization": `Bearer ${this.tokenValue}` };
      if (this.traceparentValue) headers["traceparent"] = this.traceparentValue;
      return headers;
    });

    if (this.isNewValue) {
      // Create a new empty dashboard
      this.initializeNewDashboard(R);
    } else {
      // Load existing dashboard
      this.loadExistingDashboard(R);
    }
  }

  initializeNewDashboard(R) {
    this.element.innerHTML = ""; // Clear spinner
    this.revealView = new R.RevealView(this.element);
    const dashboard = new R.RVDashboard();
    this.revealView.dashboard = dashboard;

    this.configureRevealView();
  }

  loadExistingDashboard(R) {
    R.RVDashboard.loadDashboard(this.dashboardIdValue, (dashboard) => {
      this.element.innerHTML = ""; // Clear spinner
      this.revealView = new R.RevealView(this.element);
      this.revealView.dashboard = dashboard;
      this.configureRevealView();
    }, (error) => {
      captureRevealError("dashboard load", error, { dashboardId: this.dashboardIdValue });
      this.renderLoadFailure();
    });
  }

  renderLoadFailure() {
    this.element.innerHTML = `
      <div class="alert alert-danger">
        Failed to load dashboard. Please try again later.
      </div>
    `;
  }

  configureRevealView() {
    // We persist via PUT /dashboards/{id}; at the SDK default (true) saveFinished()
    // fires the SDK's own POST /DashboardFile and writes a duplicate version row.
    this.revealView.serverSideSave = false;

    // Configure view options based on user permissions
    this.revealView.canEdit = this.canEditValue;
    this.revealView.canSaveAs = this.canSaveAsValue;
    this.revealView.startInEditMode = this.startInEditModeValue;
    this.revealView.showAIChat = this.aiEnabledValue;

    // Always allow exports
    this.revealView.showExportToPDF = true;
    this.revealView.showExportToExcel = true;
    this.revealView.showExportToImage = true;
    this.revealView.showExportToCsv = true;

    // Configure available data sources for the "Add Data Source" dialog
    this.revealView.onDataSourcesRequested = (callback) => {
      const R = window.Reveal;
      const postgresDataSource = new R.RVPostgresDataSource();
      postgresDataSource.id = "scientist-data-warehouse";
      postgresDataSource.title = "Scientist Data Warehouse";

      // Build Excel data source items from server-provided datasource list
      const excelDatasources = (this.datasourcesValue || []).filter(ds => ds.type === "excel");
      const excelItems = excelDatasources.map(ds => {
        const localFile = new R.RVLocalFileDataSourceItem();
        localFile.uri = `local:/${ds.name}`;
        const excelItem = new R.RVExcelDataSourceItem(localFile);
        excelItem.title = ds.name.replace(/\.xlsx$/, "");
        return excelItem;
      });

      callback(new R.RevealDataSources([postgresDataSource], excelItems, false));
    };

    // SDK has no onSaveAs event: both Save and Save As arrive via onSave, discriminated by args.saveAs.
    if (this.canEditValue || this.canSaveAsValue) {
      this.revealView.onSave = (rv, args) => this.handleSave(rv, args);
    }
    this.dispatch("ready");
  }

  startTokenRefresh() {
    const intervalMs = this.refreshIntervalMinutesValue * 60 * 1000;
    this.refreshInterval = setInterval(() => this.refreshToken(), intervalMs);
  }

  async refreshToken() {
    try {
      const response = await fetch(this.refreshUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        credentials: "same-origin"
      });
      if (response.ok) {
        const data = await response.json();
        this.tokenValue = data.token;
        updateRevealAIToken(data.token);
        this.dispatch("token-refreshed");
      } else if (response.status === 401) {
        this.stopTokenRefresh();
        this.element.innerHTML = `
          <div class="alert alert-warning">
            Your session has expired. Please <a href="/login">log in again</a> to continue.
          </div>
        `;
      } else {
        console.error(`Reveal token refresh failed: HTTP ${response.status}`); // eslint-disable-line no-console
      }
    } catch (error) {
      // Will retry on next interval, but the token is stale until then.
      console.error("Reveal token refresh failed:", error); // eslint-disable-line no-console
    }
  }

  stopTokenRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  handleSave(rv, args) {
    if (args.saveAs) {
      if (!this.canSaveAsValue) return;
      this.forkDashboard(args);
    } else {
      if (!this.canEditValue) return;
      this.saveDashboardToServer(this.dashboardIdValue, args);
    }
  }

  forkDashboard(args) {
    const newId = generateUuid();
    const copiedFrom = this.dashboardIdValue || null;
    this.saveDashboardToServer(newId, args, { copiedFrom, isFork: true });
  }

  saveDashboardToServer(dashboardId, args, { copiedFrom = null, isFork = false } = {}) {
    const query = copiedFrom ? `?copied_from=${copiedFrom}` : "";
    const url = `${this.serverUrlValue}/dashboards/${dashboardId}${query}`;

    // The name typed in the Save As dialog lives only on args.name; serializeWithNewName stamps it into the .rdash Title.
    const serialize = (callback) => {
      if (isFork && args.name) {
        args.serializeWithNewName(args.name, callback);
      } else {
        args.serialize(callback);
      }
    };

    serialize((bytes) => {
      let failureDetail = "";
      fetch(url, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${this.tokenValue}`,
          "Content-Type": "application/octet-stream"
        },
        body: bytes
      })
        .then(async (response) => {
          if (!response.ok) {
            const text = await response.text();
            // Preserve the existing user-facing detail, but never put the body in an
            // Error or console breadcrumb that Sentry could subsequently serialize.
            try {
              failureDetail = JSON.parse(text).error || "";
            } catch { /* ignore parse failures */ }
            throw new Error(`Save failed: ${response.status}`);
          }
          // saveFinished() synchronously runs the SDK's edit-mode teardown; a throw in there must not be reported as a failed save.
          try {
            args.saveFinished();
          } catch (finishError) {
            console.error("Reveal saveFinished() failed after a successful save:", finishError); // eslint-disable-line no-console
          }
          if (isFork && this.saveAsRedirectUrlValue) {
            this.showToast("Dashboard copied. Opening your copy...");
            this.uploadThumbnail(dashboardId);
            const redirectUrl = this.saveAsRedirectUrlValue.replace("__ID__", dashboardId);
            window.location.assign(redirectUrl);
          } else {
            this.showToast(isFork ? "Dashboard copied" : "Dashboard saved successfully");
            this.uploadThumbnail(dashboardId);
            if (!isFork) this.dispatch("saved");
          }
        })
        .catch((error) => {
          const safeError = sanitizeRevealSaveError(error);
          console.error("Failed to save dashboard:", safeError); // eslint-disable-line no-console
          captureRevealError(isFork ? "dashboard fork" : "dashboard save", safeError, { dashboardId });
          const msg = failureDetail
            ? `Failed to save dashboard: ${failureDetail}`
            : "Failed to save dashboard. Please try again.";
          this.showToast(msg, true);
        });
    });
  }

  uploadThumbnail(dashboardId) {
    if (!this.revealView) return;

    this.revealView.toImage().then((image) => {
      // toImage() returns an <img> element, extract the src attribute
      const dataUrl = image?.src;
      
      if (!dataUrl || typeof dataUrl !== "string") {
        console.error("toImage returned unexpected result:", image); // eslint-disable-line no-console
        return;
      }
      fetch(`${this.serverUrlValue}/dashboards/${dashboardId}/thumbnail`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${this.tokenValue}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ thumbnail: dataUrl })
      }).catch((error) => {
        console.error("Failed to upload thumbnail:", error); // eslint-disable-line no-console
      });
    }).catch((error) => {
      console.error("Failed to capture thumbnail:", error); // eslint-disable-line no-console
      console.error("Error details:", error.message, error.stack); // eslint-disable-line no-console
    });
  }

  showToast(message, isError = false) {
    const toast = document.createElement("div");
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 24px;
      border-radius: 4px;
      color: white;
      background-color: ${isError ? "#dc3545" : "#28a745"};
      z-index: 9999;
      opacity: 1;
      transition: opacity 0.3s ease;
    `;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = "0";
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }

  disconnect() {
    this.stopTokenRefresh();
  }

}
